// Roadmap — read-only product roadmap for retailers.
//
// Mirrors the admin Roadmap page but with NO edit controls: retailers see
// what's shipped, in progress and planned, but cannot change anything.
//
// Backend: GET /api/portal/roadmap (modules/roadmap.js, migration 0035).

import { ApiError } from "@tkos/api-client";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

type Status = "planned" | "building" | "done";

interface Feature {
  id: string;
  name: string;
  description: string;
  status: Status;
}

interface Version {
  id: string;
  code: string;
  title: string;
  subtitle: string;
  status: string;
  features: Feature[];
}

interface Stats {
  done: number;
  building: number;
  planned: number;
  total: number;
}

interface RoadmapResponse {
  ok?: boolean;
  versions?: Version[];
  stats?: Stats;
}

type Filter = "all" | Status;

const STATUS_LABEL: Record<Status, string> = {
  planned: "Planned",
  building: "Building",
  done: "Shipped",
};

function coerceStatus(raw: unknown): Status {
  const v = String(raw ?? "").toLowerCase().trim();
  return v === "done" || v === "building" ? v : "planned";
}

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"']/g, (c) => {
    const m: Record<string, string> = {
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
    };
    return m[c] ?? c;
  });
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

const page: Page = {
  name: "roadmap",
  label: "Roadmap",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ac = new AbortController();
    let versions: Version[] = [];
    let stats: Stats = { done: 0, building: 0, planned: 0, total: 0 };
    let loading = true;
    let error: string | null = null;
    let filter: Filter = "all";

    function render(): void {
      if (loading) {
        container.innerHTML = `<section class="rrm"><div class="rrm-loading">Loading roadmap…</div></section>`;
        return;
      }
      if (error) {
        container.innerHTML = `<section class="rrm"><div class="rrm-error">${esc(error)}</div></section>`;
        return;
      }

      const statCards = [
        { label: "Shipped", val: stats.done, cls: "done" },
        { label: "In progress", val: stats.building, cls: "building" },
        { label: "Planned", val: stats.planned, cls: "planned" },
        { label: "Total", val: stats.total, cls: "total" },
      ]
        .map(
          (s) => `
          <div class="rrm-stat rrm-stat--${s.cls}">
            <div class="rrm-stat-val">${s.val}</div>
            <div class="rrm-stat-label">${s.label}</div>
          </div>`,
        )
        .join("");

      const filterPills = (["all", "done", "building", "planned"] as Filter[])
        .map((f) => {
          const label = f === "all" ? "All" : STATUS_LABEL[f];
          return `<button type="button" class="rrm-pill${filter === f ? " rrm-pill--on" : ""}" data-filter="${f}">${label}</button>`;
        })
        .join("");

      const versionsHtml = versions
        .map((v) => {
          const shown = v.features.filter((f) => filter === "all" || f.status === filter);
          const rows = shown.length
            ? shown
                .map(
                  (f) => `
              <li class="rrm-feature rrm-feature--${f.status}">
                <span class="rrm-dot" aria-hidden="true"></span>
                <div class="rrm-feature-body">
                  <div class="rrm-feature-name">${esc(f.name)}</div>
                  ${f.description ? `<div class="rrm-feature-desc">${esc(f.description)}</div>` : ""}
                </div>
                <span class="rrm-badge rrm-badge--${f.status}">${STATUS_LABEL[f.status]}</span>
              </li>`,
                )
                .join("")
            : `<li class="rrm-feature rrm-feature--empty">No features match this filter.</li>`;
          return `
          <section class="rrm-version">
            <header class="rrm-version-head">
              <div class="rrm-version-title">
                <span class="rrm-version-code">${esc(v.code)}</span>
                <span class="rrm-version-name">${esc(v.title)}</span>
                <span class="rrm-badge rrm-badge--${esc(v.status)}">${esc(STATUS_LABEL[coerceStatus(v.status)])}</span>
              </div>
              ${v.subtitle ? `<div class="rrm-version-sub">${esc(v.subtitle)}</div>` : ""}
            </header>
            <ul class="rrm-features">${rows}</ul>
          </section>`;
        })
        .join("");

      container.innerHTML = `
        <section class="rrm">
          <header class="rrm-header">
            <h1>Roadmap</h1>
            <p class="rrm-sub">What's shipped, what we're building, and what's coming next for The Kitchen OS.</p>
          </header>
          <div class="rrm-stats">${statCards}</div>
          <div class="rrm-filters">${filterPills}</div>
          <div class="rrm-versions">${versionsHtml}</div>
        </section>`;
    }

    async function load(): Promise<void> {
      loading = true;
      error = null;
      render();
      try {
        const resp = await ctx.api<RoadmapResponse>("/portal/roadmap", { signal: ac.signal });
        versions = (resp.versions ?? []).map((v) => ({
          ...v,
          features: (v.features ?? []).map((f) => ({ ...f, status: coerceStatus(f.status) })),
        }));
        stats = resp.stats ?? { done: 0, building: 0, planned: 0, total: 0 };
        loading = false;
        render();
      } catch (err) {
        if (isAbortError(err)) return;
        loading = false;
        error =
          err instanceof ApiError ? `Couldn't load the roadmap (${err.status}).` : "Couldn't load the roadmap.";
        render();
      }
    }

    function onClick(e: MouseEvent): void {
      const pill = (e.target as HTMLElement | null)?.closest<HTMLElement>("[data-filter]");
      if (pill) {
        filter = (pill.dataset["filter"] as Filter) ?? "all";
        render();
      }
    }

    container.addEventListener("click", onClick);
    void load();

    return () => {
      ac.abort();
      container.removeEventListener("click", onClick);
    };
  },
};

registerPage(page);

export default page;
